// Alerts (Plan 4c, S-w): each user's preferences per ANM entity × channel, and the one-click unsubscribe.
import { z } from 'zod';
import { Id } from './accounts.ts';

export const AlertChannel = z.enum(['email', 'whatsapp']);
export type AlertChannel = z.infer<typeof AlertChannel>;

/** The kinds of event a user chooses for (domain `ALERT_EVENT_KINDS`). */
export const AlertEventKind = z.enum(['prazos', 'dou', 'scm', 'ambiental', 'pedidos', 'sei']);
export type AlertEventKind = z.infer<typeof AlertEventKind>;

/** How one kind of event reaches the user: an e-mail as it appears, and/or a line in each digest. */
export const EventDelivery = z.object({ immediate: z.boolean(), daily: z.boolean(), weekly: z.boolean(), monthly: z.boolean() });
export type EventDelivery = z.infer<typeof EventDelivery>;

/** One preference: `enabled` = the channel on/off; D-n reminders (0–90 days, at most 6); per kind of event, how. */
export const AlertPreference = z.object({
  enabled: z.boolean(),
  reminderDays: z.array(z.number().int().min(0).max(90)).max(6),
  events: z.object({ prazos: EventDelivery, dou: EventDelivery, scm: EventDelivery, ambiental: EventDelivery, pedidos: EventDelivery, sei: EventDelivery }),
});
export type AlertPreference = z.infer<typeof AlertPreference>;

export const AlertChannelInfo = z.object({
  channel: AlertChannel,
  /** False for a channel with no provider yet (WhatsApp at launch): shown as "em breve", can't be switched on. */
  available: z.boolean(),
});

/** A channel's setting at one level: what applies, and whether it was set at this level (else inherited). */
export const AlertChannelSetting = z.object({
  channel: AlertChannel,
  effective: AlertPreference,
  /** The stored value at this level; null = inherited (entity → the user's default; default → the S-w defaults). */
  own: AlertPreference.nullable(),
});

export const AlertPreferencesResponse = z.object({
  channels: z.array(AlertChannelInfo),
  defaults: z.array(AlertChannelSetting),
  entities: z.array(z.object({
    anmEntityId: Id,
    /** The holder's name as published, in title case. */
    label: z.string(),
    processCount: z.number().int().nonnegative(),
    settings: z.array(AlertChannelSetting),
  })),
});
export type AlertPreferencesResponse = z.infer<typeof AlertPreferencesResponse>;

/**
 * Sets the user's default (`anmEntityId` null) or one entity's override for a channel. `preference: null` removes the
 * stored value (an entity goes back to the default; the default goes back to S-w's).
 */
export const AlertPreferenceUpdate = z.object({
  channel: AlertChannel,
  anmEntityId: Id.nullable(),
  preference: AlertPreference.nullable(),
});
export type AlertPreferenceUpdate = z.infer<typeof AlertPreferenceUpdate>;
export const AlertPreferenceUpdateResponse = z.object({ ok: z.literal(true) });

export const UnsubscribeQuery = z.object({ t: z.string().min(10).max(1000) });

/** What a valid unsubscribe link turns off (GET), or did (POST). */
export const UnsubscribeResponse = z.object({
  anmEntityId: Id,
  /** The holder's name, title case — "alertas de <label> por e-mail". */
  label: z.string(),
  channel: AlertChannel,
  /** Whether that entity × channel is on now (GET before confirming; false after POST). */
  enabled: z.boolean(),
});
export type UnsubscribeResponse = z.infer<typeof UnsubscribeResponse>;
